import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  HomeIcon, CalendarDaysIcon, BanknotesIcon, ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon, UserGroupIcon, BookOpenIcon, MegaphoneIcon,
  UserCircleIcon, AcademicCapIcon, DocumentChartBarIcon, TableCellsIcon,
  XMarkIcon, ChartBarIcon, ChatBubbleLeftRightIcon, PhotoIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useSchoolBrand } from '../../hooks/useSchoolBrand';
import { useAllModules } from '../../hooks/useAccess';
import { useSchoolModules } from '../../hooks/useSchoolModules';

const navigation = [
  { name: 'Dashboard',          href: '/teacher/dashboard',          icon: HomeIcon,                   module: 'dashboard', alwaysShow: true },
  { name: 'My Classes',         href: '/teacher/classes',            icon: UserGroupIcon,              module: 'classes', alwaysShow: true },
  { name: 'My Attendance',      href: '/teacher/attendance',         icon: CalendarDaysIcon,           module: 'attendance' },
  { name: 'Assessments',        href: '/teacher/assessments',        icon: AcademicCapIcon,            module: 'assessments' },
  { name: 'Term Exams',         href: '/teacher/term-exams',         icon: ClipboardDocumentListIcon,  module: 'assessments' },
  { name: 'Feedback Results',   href: '/teacher/feedback',           icon: ChartBarIcon,               module: 'assessments' },
  { name: 'CLO Attainment',     href: '/teacher/attainment',         icon: ChartBarIcon,               module: 'assessments', obeOnly: true },
  { name: 'PLO Attainment',     href: '/teacher/plo-attainment',     icon: ChartBarIcon,               module: 'assessments', obeOnly: true },
  { name: 'Grade Reports',      href: '/teacher/grade-report',       icon: DocumentChartBarIcon,       module: 'gradeReports' },
  { name: 'Student Attendance', href: '/teacher/student-attendance', icon: CalendarDaysIcon,           module: 'studentAttendance' },
  { name: 'Timetable',          href: '/teacher/timetable',          icon: TableCellsIcon,             module: 'timetable', alwaysShow: true },
  { name: 'Student Leaves',     href: '/teacher/student-leaves',     icon: ClipboardDocumentCheckIcon, module: 'studentAttendance', alwaysShow: true },
  { name: 'Datesheets',         href: '/teacher/datesheets',         icon: TableCellsIcon,             module: 'datesheets' },
  { name: 'ILP',                href: '/teacher/ilp',                icon: ClipboardDocumentListIcon,  module: 'ilp', alwaysShow: true },
  { name: 'Materials',          href: '/teacher/materials',          icon: BookOpenIcon,               module: 'materials' },
  { name: 'Class Gallery',      href: '/teacher/class-media',        icon: PhotoIcon,                  module: 'classMedia', alwaysShow: true },
  { name: 'AI Quizzes',         href: '/teacher/quizzes',            icon: ClipboardDocumentListIcon,  module: 'quizzes' },
  { name: 'Notices',            href: '/teacher/notices',            icon: MegaphoneIcon,              badge: 'notices',  module: 'notices' },
  { name: 'Messages',           href: '/teacher/messages',           icon: ChatBubbleLeftRightIcon,    badge: 'messages', module: 'messages' },
  { name: 'Salary',             href: '/teacher/salary',             icon: BanknotesIcon,              module: 'salary' },
  { name: 'Leaves',             href: '/teacher/leaves',             icon: ClipboardDocumentCheckIcon, module: 'leaves' },
  { name: 'Profile',            href: '/teacher/profile',            icon: UserCircleIcon,             module: 'profile' },
];

const buildSections = (obeEnabled) => [
  { label: 'Main',     items: navigation.filter(n => ['Dashboard','My Classes','My Attendance'].includes(n.name)) },
  { label: 'Academic', items: navigation.filter(n => {
      if (['Assessments','Term Exams','Feedback Results','CLO Attainment','PLO Attainment','Grade Reports','Student Attendance','Student Leaves','Timetable','Datesheets','ILP'].includes(n.name)) {
        if (n.obeOnly) return obeEnabled;
        return true;
      }
      return false;
    })
  },
  { label: 'Content',  items: navigation.filter(n => ['Materials','Class Gallery','AI Quizzes','Notices','Messages'].includes(n.name)) },
  { label: 'Personal', items: navigation.filter(n => ['Salary','Leaves','Profile'].includes(n.name)) },
];

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .sidebar { width:240px; min-width:240px; min-height:100vh; background:#0f1123; display:flex; flex-direction:column; overflow-x:hidden; font-family:'Sora',sans-serif; border-right:1px solid rgba(255,255,255,.04); }
  .sidebar-close { display:none; }
  @media (max-width:768px) {
    .sidebar-close { display:flex; align-items:center; justify-content:center; position:absolute; top:16px; right:16px; width:28px; height:28px; border-radius:8px; background:rgba(255,255,255,.08); border:none; cursor:pointer; color:white; }
    .sidebar-close svg { width:15px; height:15px; stroke:white; stroke-width:2; }
    .sidebar-brand { position:relative; }
  }
  .sidebar-brand { padding:24px 20px 20px; border-bottom:1px solid rgba(255,255,255,.06); flex-shrink:0; }
  .sidebar-brand-icon { width:34px; height:34px; background:#10c98e; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:10px; }
  .sidebar-brand-icon svg { width:18px; height:18px; fill:none; stroke:white; stroke-width:2; }
  .sidebar-brand-name { font-size:.95rem; font-weight:700; color:#fff; letter-spacing:-.02em; line-height:1; }
  .sidebar-brand-sub  { font-size:.68rem; color:#4a5075; margin-top:3px; letter-spacing:.04em; text-transform:uppercase; }
  .sidebar-nav { flex:1; padding:12px; overflow-y:auto; overflow-x:hidden; scrollbar-width:none; }
  .sidebar-nav::-webkit-scrollbar { display:none; }
  .sidebar-section-label { font-size:.62rem; font-weight:600; color:#353b5c; text-transform:uppercase; letter-spacing:.08em; padding:16px 8px 6px; }
  .sidebar-link { display:flex; align-items:center; gap:10px; padding:9px 10px; border-radius:10px; text-decoration:none; color:#7880a4; font-size:.82rem; font-weight:500; transition:background .15s,color .15s; margin-bottom:2px; position:relative; }
  .sidebar-link-text { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .sidebar-link:hover { background:rgba(255,255,255,.04); color:#c8ccde; }
  .sidebar-link.active { background:rgba(16,201,142,.12); color:#10c98e; }
  .sidebar-link.active::before { content:''; position:absolute; left:-12px; top:25%; bottom:25%; width:3px; background:#10c98e; border-radius:0 3px 3px 0; }
  .sidebar-link svg { width:17px; height:17px; stroke-width:1.7; flex-shrink:0; }
  .sidebar-footer { padding:16px 12px; border-top:1px solid rgba(255,255,255,.05); flex-shrink:0; }
  .sidebar-footer-text { font-size:.68rem; color:#353b5c; text-align:center; letter-spacing:.02em; }
  .notice-badge { margin-left:auto; min-width:18px; height:18px; padding:0 5px; border-radius:99px; background:#ef4444; color:white; font-size:.6rem; font-weight:700; display:flex; align-items:center; justify-content:center; line-height:1; flex-shrink:0; }
`;

const TeacherSidebar = ({ onClose }) => {
  const location = useLocation();
  const { brand } = useSchoolBrand();
  const { user }  = useAuth();
  const { modules: allowedModules } = useAllModules();
  const { cloPloEnabled } = useSchoolModules();
  const [noticeCount,  setNoticeCount]  = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [obeEnabled,   setObeEnabled]   = useState(false);

  useEffect(() => {
    api.get('/teacher/notices/unread-count')
      .then(r => setNoticeCount(r.data.count || 0))
      .catch(() => {});
    api.get('/teacher/obe-settings')
      .then(r => setObeEnabled(!!r.data?.obeEnabled && !!r.data?.obeTeacherEnabled))
      .catch(() => {});

    const fetchMessageCount = () => api.get('/chat/unread-count').then(r => setMessageCount(r.data.unreadCount || 0)).catch(() => {});
    fetchMessageCount();
    const id = setInterval(fetchMessageCount, 20000);
    // ChatPage dispatches this the instant a thread gets marked read, so the
    // badge clears immediately instead of waiting up to 20s for the next poll.
    window.addEventListener('chat-unread-changed', fetchMessageCount);
    return () => { clearInterval(id); window.removeEventListener('chat-unread-changed', fetchMessageCount); };
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/teacher/notices')) setNoticeCount(0);
  }, [location.pathname]);

  const getBadgeCount = (item) => item.badge === 'notices' ? noticeCount : item.badge === 'messages' ? messageCount : 0;

  // OBE nav requires both the Admin-level obe_enabled toggle AND the
  // Super-Admin cloPloEnabled module switch.
  const sections = buildSections(obeEnabled && cloPloEnabled);

  return (
    <>
      <style>{style}</style>
      <div className="sidebar">
        <div className="sidebar-brand">
          {brand.logo
            ? <img src={brand.logo} alt="logo" style={{width:34,height:34,borderRadius:10,objectFit:'contain',background:'rgba(255,255,255,.95)',padding:2,border:'1px solid rgba(255,255,255,.12)',flexShrink:0}}/>
            : <div className="sidebar-brand-icon">
                <svg viewBox="0 0 24 24"><path d="M12 3L2 9l10 6 10-6-10-6zM2 15l10 6 10-6M2 12l10 6 10-6" /></svg>
              </div>
          }
          <div>
            <div className="sidebar-brand-name">{brand.name || 'School Portal'}</div>
            <div className="sidebar-brand-sub">Teacher Panel</div>
          </div>
          {onClose && <button className="sidebar-close" onClick={onClose}><XMarkIcon /></button>}
        </div>
        <nav className="sidebar-nav">
          {sections.map(sec => (
            sec.items.filter(item => {
              if (item.obeOnly || item.alwaysShow) return true;
              return !allowedModules || allowedModules.includes(item.module);
            }).length > 0 && (
              <div key={sec.label}>
                <div className="sidebar-section-label">{sec.label}</div>
                {sec.items.filter(item => {
                  if (item.obeOnly || item.alwaysShow) return true;
                  return !allowedModules || allowedModules.includes(item.module);
                }).map(item => {
                  const isActive = location.pathname.startsWith(item.href);
                  return (
                    <Link key={item.name} to={item.href}
                      className={`sidebar-link${isActive ? ' active' : ''}`}
                      onClick={onClose}>
                      <item.icon />
                      <span className="sidebar-link-text">{item.name}</span>
                      {item.badge && getBadgeCount(item) > 0 && (
                        <span className="notice-badge">{getBadgeCount(item) > 99 ? '99+' : getBadgeCount(item)}</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-footer-text">v1.0.0 · Teacher Portal</div>
        </div>
      </div>
    </>
  );
};

export default TeacherSidebar;